import type { Coding, Organization, Reference, Resource, ResourceType } from '@medplum/fhirtypes';

/**
 * Every FHIR resource type that carries `meta.account` (i.e. is tenant-scoped
 * via {@link withTenantScope}). Ordered so dependents come before the
 * resources they reference, which makes it safe for teardown scripts to
 * iterate top-to-bottom and delete each type without hitting referential-
 * integrity errors.
 *
 * **This is the single source of truth for tenant-scoped teardown.** Both
 * the E2E cleanup (`e2e/cleanup.ts`), the preview env teardown
 * (`apps/provider-app/scripts/teardown-env.ts`), and the env wipe script
 * (`apps/provider-app/scripts/wipe-env.ts`) iterate this list. When a new
 * bot or flow starts creating a new tenant-scoped resource type, add it
 * here — above whatever it references — and every teardown path picks it
 * up automatically.
 *
 * Excludes:
 *   - Env-tagged admin metadata (`Bot`, `Subscription`, `AccessPolicy`,
 *     `ClientApplication`) — these live outside tenant compartments and
 *     are handled separately by env-tag sweeps.
 *   - Core profile types (`Patient`, `Practitioner`) — these are included
 *     at the end of the list; teardown scripts must delete their
 *     `ProjectMembership` rows before deleting the profile itself.
 */
export const TENANT_SCOPED_RESOURCE_TYPES: ResourceType[] = [
  // Leaf / dependent resources first
  'Slot',
  'Schedule',
  'HealthcareService',
  'ChargeItem',
  'Claim',
  'GuidanceResponse',
  'CoverageEligibilityResponse',
  'CoverageEligibilityRequest',
  'DocumentReference',
  'Provenance',
  'Communication',
  'Consent',
  'DiagnosticReport',
  'Observation',
  'MedicationRequest',
  'DeviceRequest',
  'Device',
  'Appointment',
  'Task',
  'RequestGroup',
  'CarePlan',
  'ClinicalImpression',
  'Encounter',
  'Condition',
  'Coverage',
  'RelatedPerson',
  'ServiceRequest',
  'QuestionnaireResponse',
  'Composition',
  'AuditEvent',
  // Roles and auxiliary actors
  'PractitionerRole',
  // Auxiliary Organizations (referring practices, insurance payors) created
  // via `withAuxiliaryOrgTag`. These live in the tenant org's compartment;
  // the tenant Organization itself is never in its own compartment, so this
  // is safe provided the caller deletes the tenant org separately.
  'Organization',
  // Core profiles last (their memberships must be cleaned up beforehand)
  'Patient',
  'Practitioner',
];

/**
 * Tag system used to mark auxiliary Organizations (insurance payors, referring practices).
 * Organizations WITHOUT this tag are considered tenant Organizations.
 */
export const AUXILIARY_ORG_TAG_SYSTEM = 'https://polarissleep.com/org-role';
export const AUXILIARY_ORG_TAG_CODE = 'auxiliary';

export const AUXILIARY_ORG_TAG: Coding = {
  system: AUXILIARY_ORG_TAG_SYSTEM,
  code: AUXILIARY_ORG_TAG_CODE,
  display: 'Auxiliary Organization',
};

/**
 * Builds a `meta.account` reference for the given tenant Organization ID.
 */
export function getTenantAccountRef(orgId: string | undefined): Reference<Organization> | undefined {
  if (!orgId) {
    return undefined;
  }
  return { reference: `Organization/${orgId}` };
}

/**
 * Returns a copy of the resource with `meta.account` set to the tenant Organization.
 * If `orgId` is not provided, the resource is returned unchanged.
 *
 * Use this to scope every created clinical resource to a tenant.
 */
export function withTenantScope<T extends Resource>(resource: T, orgId: string | undefined): T {
  const accountRef = getTenantAccountRef(orgId);
  if (!accountRef) {
    return resource;
  }
  return {
    ...resource,
    meta: {
      ...resource.meta,
      account: accountRef,
    },
  };
}

/**
 * Returns a copy of an Organization resource with the auxiliary tag applied.
 * Use this when creating Organizations that are NOT tenants (insurance companies, referring practices).
 */
export function withAuxiliaryOrgTag<T extends Resource>(resource: T): T {
  const existingTags = resource.meta?.tag ?? [];
  const alreadyTagged = existingTags.some(
    (tag) => tag.system === AUXILIARY_ORG_TAG_SYSTEM && tag.code === AUXILIARY_ORG_TAG_CODE
  );
  if (alreadyTagged) {
    return resource;
  }
  return {
    ...resource,
    meta: {
      ...resource.meta,
      tag: [...existingTags, AUXILIARY_ORG_TAG],
    },
  };
}
